import React, { useState, useMemo, useRef } from 'react';
import { X, Camera, Search, Check, Users, Loader2, Image as ImageIcon } from 'lucide-react';
import { ChatAvatar } from './ChatAvatar.jsx';
import { isGroupJid } from '../../../lib/sortChats';
import { uploadWhatsAvatar } from '../../../lib/whatsProfile';
import { formatWhatsAppNumber } from './UserProfilePanel.jsx';

export default function WhatsCreateGroupModal({ currentChat, allChats = [], onClose, onCreateGroup }) {
  const [subject, setSubject] = useState('');
  const [search, setSearch] = useState('');
  const [selectedJids, setSelectedJids] = useState(() => {
    const initial = new Set();
    if (currentChat?.chat_jid && !isGroupJid(currentChat.chat_jid)) {
      initial.add(currentChat.chat_jid);
    }
    return initial;
  });

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  // Available 1-on-1 contacts
  const availableContacts = useMemo(() => {
    return allChats.filter((c) => c.chat_jid && !isGroupJid(c.chat_jid));
  }, [allChats]);

  // Filtered contacts based on search query
  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return availableContacts;
    return availableContacts.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const num = formatWhatsAppNumber(c.chat_jid).toLowerCase();
      const jid = String(c.chat_jid).toLowerCase();
      return name.includes(term) || num.includes(term) || jid.includes(term);
    });
  }, [availableContacts, search]);

  const toggleSelect = (jid) => {
    setSelectedJids((prev) => {
      const next = new Set(prev);
      if (next.has(jid)) {
        next.delete(jid);
      } else {
        next.add(jid);
      }
      return next;
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const objectUrl = URL.createObjectURL(file);
    setAvatarPreview(objectUrl);
  };

  const removeAvatar = (e) => {
    e.stopPropagation();
    setAvatarFile(null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      setErrorMsg('Por favor ingresa un nombre para el grupo');
      return;
    }
    if (selectedJids.size === 0) {
      setErrorMsg('Debes seleccionar al menos un participante');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMsg('');

      let uploadedUrl = null;
      if (avatarFile) {
        try {
          uploadedUrl = await uploadWhatsAvatar(avatarFile);
        } catch (uploadErr) {
          console.warn('No se pudo subir el avatar del grupo, continuando sin foto:', uploadErr);
        }
      }

      await onCreateGroup({
        subject: subject.trim(),
        participants: Array.from(selectedJids),
        avatarUrl: uploadedUrl || null,
      });

      onClose();
    } catch (err) {
      setErrorMsg(err.message || 'Error al crear el grupo en WhatsApp');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="whats-link-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(3px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        className="whats-link-modal-panel"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
          padding: 0,
        }}
      >
        {/* Cabecera */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#dcfce7',
                color: '#15803d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Users size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                Crear nuevo grupo
              </h3>
              <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                {selectedJids.size} participante{selectedJids.size === 1 ? '' : 's'} seleccionado{selectedJids.size === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              color: '#64748b',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
            {errorMsg && (
              <div
                style={{
                  padding: '10px 14px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#b91c1c',
                  fontSize: '12.5px',
                }}
              >
                {errorMsg}
              </div>
            )}

            {/* Foto de grupo y Nombre */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />

              <div
                onClick={() => !isSubmitting && fileInputRef.current?.click()}
                title="Subir foto del grupo"
                style={{
                  position: 'relative',
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  border: '2px dashed #cbd5e1',
                  backgroundColor: avatarPreview ? '#000' : '#f1f5f9',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#16a34a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
              >
                {avatarPreview ? (
                  <>
                    <img
                      src={avatarPreview}
                      alt="Grupo preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      onClick={removeAvatar}
                      title="Quitar foto"
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: 'rgba(0,0,0,0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        color: '#fff',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <X size={11} />
                    </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', color: '#64748b' }}>
                    <Camera size={20} />
                    <span style={{ fontSize: '9px', fontWeight: '600' }}>FOTO</span>
                  </div>
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Nombre del grupo *
                </label>
                <input
                  type="text"
                  maxLength={100}
                  placeholder="Ej. Proyecto Colonny, Familia..."
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={isSubmitting}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13.5px',
                    color: '#0f172a',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#16a34a';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                />
              </div>
            </div>

            {/* Chips de contactos seleccionados */}
            {selectedJids.size > 0 && (
              <div>
                <div style={{ fontSize: '11.5px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>
                  Participantes seleccionados ({selectedJids.size}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '70px', overflowY: 'auto' }}>
                  {Array.from(selectedJids).map((jid) => {
                    const c = allChats.find((item) => item.chat_jid === jid) || {
                      chat_jid: jid,
                      name: formatWhatsAppNumber(jid),
                    };
                    const isPreselected = jid === currentChat?.chat_jid;
                    return (
                      <div
                        key={jid}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '3px 8px',
                          backgroundColor: isPreselected ? '#dcfce7' : '#f1f5f9',
                          border: isPreselected ? '1px solid #86efac' : '1px solid #e2e8f0',
                          borderRadius: '16px',
                          fontSize: '11.5px',
                          color: isPreselected ? '#15803d' : '#1e293b',
                          fontWeight: '500',
                        }}
                      >
                        <span style={{ maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.name || formatWhatsAppNumber(jid)}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleSelect(jid)}
                          disabled={isSubmitting}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: isPreselected ? '#15803d' : '#64748b',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Buscador de contactos */}
            <div>
              <div style={{ position: 'relative' }}>
                <Search
                  size={15}
                  color="#94a3b8"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}
                />
                <input
                  type="text"
                  placeholder="Buscar contactos para añadir..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 32px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '12.5px',
                    backgroundColor: '#f8fafc',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                />
              </div>
            </div>

            {/* Lista de contactos */}
            <div
              style={{
                maxHeight: '180px',
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
              }}
            >
              {filteredContacts.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                  No se encontraron contactos.
                </div>
              ) : (
                filteredContacts.map((c) => {
                  const isSelected = selectedJids.has(c.chat_jid);
                  return (
                    <div
                      key={c.chat_jid}
                      onClick={() => !isSubmitting && toggleSelect(c.chat_jid)}
                      style={{
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <div style={{ width: '32px', height: '32px', flexShrink: 0 }}>
                          <ChatAvatar url={c.avatar_url} name={c.name} jid={c.chat_jid} isGroup={false} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.name || formatWhatsAppNumber(c.chat_jid)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            {formatWhatsAppNumber(c.chat_jid)}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          border: isSelected ? '1px solid #16a34a' : '1px solid #cbd5e1',
                          backgroundColor: isSelected ? '#16a34a' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && <Check size={12} strokeWidth={3} />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Acciones */}
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
              backgroundColor: '#f8fafc',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '12.5px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={isSubmitting || !subject.trim() || selectedJids.size === 0}
              style={{
                padding: '8px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#16a34a',
                color: '#ffffff',
                fontSize: '12.5px',
                fontWeight: '600',
                cursor: isSubmitting || !subject.trim() || selectedJids.size === 0 ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || !subject.trim() || selectedJids.size === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.25)',
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Creando grupo...</span>
                </>
              ) : (
                <>
                  <Users size={14} />
                  <span>Crear grupo ({selectedJids.size})</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
